// @spec calendar-views thread-list
/**
 * Helpers for composing user-scoped browser storage keys (localStorage, Cache
 * API names, sessionStorage entries) in famfam.
 *
 * User-scoped keys embed the authenticated user's internal id so that if a
 * user signs out and a different user signs in on the same browser, the two
 * sessions cannot read each other's persisted UX state or cached data.
 *
 * Shape:
 *   famfam:u:<userId>:<base>
 *
 * The `famfam:` prefix is preserved so `clearClientCaches` and the legacy
 * sweep in this module can identify famfam-owned keys. The `u:` segment
 * distinguishes user-scoped keys from the device-level famfam keys
 * (PWA banner state, auto-open-conference preference) that intentionally
 * persist across user sessions.
 */

/** Prefix shared by all famfam-owned browser storage keys. */
export const FAMFAM_KEY_PREFIX = 'famfam:';

/** Marker segment after `famfam:` that identifies a user-scoped key. */
const USER_SCOPE_SEGMENT = 'u:';

/**
 * Stable per-device identifier for push notification subscriptions.
 * Persists across user sessions so a single browser/device always sends
 * the same clientId, enabling the server to replace stale endpoints instead
 * of accumulating dead rows.
 */
export const PUSH_CLIENT_ID_KEY = 'famfam:push-client-id';

/** localStorage key for the user's preferred calendar hour-row height. */
export const CALENDAR_HOUR_HEIGHT_KEY = 'famfam:calendar-hour-height';

/** localStorage key for the user's preferred calendar day-column count. */
export const CALENDAR_DAY_COUNT_KEY = 'famfam:calendar-day-count';

/** localStorage key for per-device tapback emoji usage frequency. */
export const TAPBACK_FREQUENCY_KEY = 'famfam:tapback-frequency';

/**
 * Device-level famfam keys that intentionally persist across user sessions
 * (e.g., PWA install banner state, auto-open-conference device preference).
 *
 * Kept here (not only in `lib/client/clear-caches.ts`) so the legacy-sweep
 * helper in this module can identify them without depending on client-only
 * code.
 */
export const DEVICE_LEVEL_KEYS: ReadonlySet<string> = new Set([
  'famfam:pwa-install-banner-dismissed:v1',
  'famfam:pwa-install-banner-installed:v1',
  'famfam:auto-open-conference-mode',
  'famfam:conference-fired',
  'famfam:emoji-mart.frequently',
  'famfam:emoji-mart.skin',
  CALENDAR_HOUR_HEIGHT_KEY,
  CALENDAR_DAY_COUNT_KEY,
  PUSH_CLIENT_ID_KEY,
  TAPBACK_FREQUENCY_KEY,
]);

/**
 * Build a user-scoped browser storage key.
 *
 * Refuses empty/whitespace/containing-colon userIds — those would collapse
 * multiple users into the same namespace (for empty) or make the key
 * ambiguous to parse (for colon). Callers are expected to pass the
 * `session.user.id` from `useAuth()` and to skip persistence when the auth
 * status is not `'authenticated'`.
 */
export function userScopedKey(userId: string, base: string): string {
  if (!isUsableUserId(userId)) {
    throw new Error(
      `userScopedKey: refusing to build a key for userId="${userId}". ` +
        'Callers must wait for auth.status === "authenticated" before reading/writing user-scoped storage.'
    );
  }
  if (base.length === 0) {
    throw new Error('userScopedKey: base must be non-empty');
  }
  return `${FAMFAM_KEY_PREFIX}${USER_SCOPE_SEGMENT}${userId}:${base}`;
}

/**
 * Convenience wrapper for components that need a user-scoped key only when the
 * authenticated user id is usable. Returns `null` during the auth-loading or
 * signed-out window so callers can treat storage reads/writes as no-ops
 * without duplicating the `isUsableUserId` guard at every call site.
 *
 * Use this instead of `isUsableUserId(id) ? userScopedKey(id, base) : null`.
 * `userScopedKey` itself intentionally still throws on unusable ids.
 */
export function userScopedKeyOrNull(
  userId: string | null | undefined,
  base: string
): string | null {
  return isUsableUserId(userId) ? userScopedKey(userId, base) : null;
}

/**
 * True when a userId string is safe to embed in a storage key.
 *
 * Rejects empty, whitespace-only, whitespace-padded, and colon-bearing ids.
 * The trim check in particular protects the key namespace: if an upstream
 * auth source ever hands us `' user-1 '`, we'd otherwise embed that padding
 * verbatim and split the same logical account across two storage prefixes.
 */
export function isUsableUserId(userId: string | null | undefined): userId is string {
  return (
    typeof userId === 'string' &&
    userId.length > 0 &&
    userId === userId.trim() &&
    !userId.includes(':')
  );
}

/** True for any famfam-owned key (user-scoped, device-level, or legacy). */
export function isFamfamKey(key: string): boolean {
  return key.startsWith(FAMFAM_KEY_PREFIX);
}

/** True for user-scoped famfam keys (i.e. `famfam:u:<id>:...`). */
export function isUserScopedKey(key: string): boolean {
  return key.startsWith(`${FAMFAM_KEY_PREFIX}${USER_SCOPE_SEGMENT}`);
}

/**
 * True for famfam keys that predate the user-scoping migration: `famfam:*`
 * keys that are neither user-scoped nor in the device-level allowlist.
 * These are safe to sweep on the first authenticated mount.
 */
export function isLegacyFamfamKey(key: string): boolean {
  return isFamfamKey(key) && !isUserScopedKey(key) && !DEVICE_LEVEL_KEYS.has(key);
}

/** Small per-user registry — lazy init + bulk clear, no other ops. */
export interface PerUserRegistry<T> {
  /** Return the entry for `userId`, constructing it via `init` on first access. */
  getOrCreate(userId: string): T;
  /** Drop every entry. Forces re-initialization on next `getOrCreate`. */
  clearAll(): void;
}

/**
 * Create a lazy-initialized per-user registry. Both `analysis-cache.ts` and
 * `calendar-events-cache.ts` grew shape-identical `const perUserCaches = new
 * Map<string, ...>()` + `getState(userId)` helpers; this helper replaces
 * that boilerplate so any future per-user cache (threads, family config,
 * …) can reuse the same lazy-init pattern.
 */
export function createPerUserRegistry<T>(init: (userId: string) => T): PerUserRegistry<T> {
  const entries = new Map<string, T>();
  return {
    getOrCreate(userId: string): T {
      let entry = entries.get(userId);
      if (entry == null) {
        entry = init(userId);
        entries.set(userId, entry);
      }
      return entry;
    },
    clearAll(): void {
      entries.clear();
    },
  };
}
